import { Box, styled } from '@mui/material';
import { Input as FplInput } from '@figma/fpl-components';
import fplTokens from '@figma/fpl-tokens/css-vars';
import * as colorPicker from '@zag-js/color-picker';
import { ReactNode, useCallback, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { color as colors } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { I18N_KEYS } from '@/language/keys';
import { FormattedNumberInput } from '@/UI/NumberInput/FormattedNumberInput';
import { PercentFormatter } from '@/UI/NumberInput/percentFormatter';
import { Input } from '../Input/Input';
import { FlexCenVer } from '../styles';
import { parseHexCode } from './colorPicker.utils';
import type { ColorFormat } from './ColorPicker';

const ColorBox = styled('div', {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<{ bgColor: colorPicker.Color; size?: 'small' | 'large' }>(({ bgColor, size }) => ({
  width: size === 'small' ? '14px' : '18px',
  height: size === 'small' ? '14px' : '18px',
  borderRadius: '2px',
  backgroundColor: bgColor.toString('rgba'),
}));

interface ColorInputProps {
  adornment?: ReactNode;
  color: colorPicker.Color;
  disabled?: boolean;
  format?: ColorFormat;
  fullWidth?: boolean;
  /** Connect the FPL value and alpha fields inside the picker. */
  grouped?: boolean;
  innerRef?: RefObject<HTMLElement | null>;
  isOpen?: boolean;
  onChange: (color: colorPicker.Color) => void;
  onChangeOngoing?: (color: colorPicker.Color) => void;
  onColorBoxClick?: () => void;
  showAlpha?: boolean;
  showColorEditor?: boolean;
  size?: 'small' | 'large';
  withColorBox?: boolean;
}

const HEX_REGEX = /^([0-9a-fA-F]*)$/;
const DIGIT_REGEX = /^[0-9]*$/;

const LEGACY_HEX_INPUT_WIDTH = 120;
const FPL_HEX_INPUT_WIDTH = 124;
const LEGACY_NUMERIC_INPUT_WIDTH = 50;
const FPL_CHANNEL_INPUT_WIDTH = 40;

const getGroupedInputColumns = (format: ColorFormat, showAlpha: boolean) => {
  if (format === 'HEX') {
    return showAlpha ? 'minmax(0, 1fr) 56px' : '1fr';
  }

  return showAlpha ? 'repeat(3, minmax(0, 1fr)) 56px' : 'repeat(3, minmax(0, 1fr))';
};

const getValidRange = (index: number, isRgb: boolean) => {
  if (isRgb) return { min: 0, max: 255 };
  return index === 0 ? { min: 0, max: 360 } : { min: 0, max: 100 };
};

const getClosestRangeValue = (value: number, range: { min: number; max: number }) => {
  if (value < range.min) return range.min;
  if (value > range.max) return range.max;
  return value;
};

const HexEditor = ({
  color,
  onChange,
  disabled = false,
  fullWidth = false,
  grouped = false,
}: Pick<ColorInputProps, 'color' | 'onChange' | 'disabled' | 'fullWidth' | 'grouped'>) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [hexInputValue, setHexInputValue] = useState(color.toString('hex').slice(1));

  const updateHexColor = (value: string) => {
    if (!HEX_REGEX.test(value)) return;
    setHexInputValue(value);
  };

  const onBlur = () => {
    try {
      const normalizedHex = parseHexCode(hexInputValue);
      const parsedColor = colorPicker.parse(normalizedHex ?? color.toString('hex').slice(1));
      onChange(parsedColor);
      setHexInputValue(parsedColor.toString('hex').slice(1));
    } catch (_error) {
      // Ignore if color is invalid
    }
  };

  if (isFplMigrationEnabled && grouped) {
    return (
      <FplInput
        aria-label="Hex color"
        disabled={disabled}
        onBlur={onBlur}
        onChange={updateHexColor}
        value={hexInputValue}
      />
    );
  }

  return (
    <Input
      disabled={disabled}
      onBlur={onBlur}
      onChange={(e) => updateHexColor(e.target.value)}
      size="small"
      sx={
        fullWidth
          ? { flex: 1, minWidth: 0 }
          : { width: isFplMigrationEnabled ? FPL_HEX_INPUT_WIDTH : LEGACY_HEX_INPUT_WIDTH }
      }
      value={hexInputValue}
    />
  );
};

const getChannelValue = (color: colorPicker.Color, channelIndex: number, isRgb: boolean) => {
  if (isRgb) {
    return color.getChannelValue(channelIndex === 0 ? 'red' : channelIndex === 1 ? 'green' : 'blue');
  }
  return color.getChannelValue(channelIndex === 0 ? 'hue' : channelIndex === 1 ? 'saturation' : 'lightness');
};

const ChannelsEditor = ({
  color,
  disabled = false,
  onChange,
  format = 'RGB',
  fullWidth = false,
  grouped = false,
}: Pick<ColorInputProps, 'color' | 'onChange' | 'disabled' | 'fullWidth' | 'grouped'> & {
  format?: 'RGB' | 'HSL';
}) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const isRgb = format === 'RGB';
  const [channels, setChannels] = useState(
    isRgb
      ? [color.getChannelValue('red'), color.getChannelValue('green'), color.getChannelValue('blue')]
      : [color.getChannelValue('hue'), color.getChannelValue('saturation'), color.getChannelValue('lightness')],
  );

  const updateChannel = useCallback(
    (index: number, value: string) => {
      if (!DIGIT_REGEX.test(value)) return;

      if (value === '') {
        setChannels(channels.map((channel, i) => (i === index ? -1 : channel)));
        return;
      }

      const parsedValue = Number(value);

      if (isNaN(parsedValue) || parsedValue < 0) return;
      setChannels(channels.map((channel, i) => (i === index ? Math.round(parsedValue) : channel)));
    },
    [channels],
  );

  const onBlur = useCallback(() => {
    const [validChannel1, validChannel2, validChannel3] = channels.map((channel, index) => {
      if (channel === -1) return getChannelValue(color, index, isRgb);
      const range = getValidRange(index, isRgb);
      return getClosestRangeValue(channel, range);
    });

    setChannels([validChannel1, validChannel2, validChannel3]);

    onChange(
      color
        .withChannelValue(isRgb ? 'red' : 'hue', validChannel1)
        .withChannelValue(isRgb ? 'green' : 'saturation', validChannel2)
        .withChannelValue(isRgb ? 'blue' : 'lightness', validChannel3),
    );
  }, [channels, isRgb, onChange, color]);

  if (isFplMigrationEnabled && grouped) {
    return (
      <>
        {channels.map((channel, index) => (
          <FplInput.Root key={index}>
            <span aria-hidden style={{ width: `calc(${fplTokens.spacer.x1} / 2)`, flexShrink: 0 }} />
            <FplInput
              aria-label={`${format} channel ${index + 1}`}
              disabled={disabled}
              onBlur={onBlur}
              onChange={(value) => updateChannel(index, value)}
              value={channel === -1 ? '' : String(Math.round(channel))}
            />
            <span aria-hidden style={{ width: `calc(${fplTokens.spacer.x1} / 2)`, flexShrink: 0 }} />
          </FplInput.Root>
        ))}
      </>
    );
  }

  const inputSx = fullWidth
    ? { flex: 1, minWidth: 0 }
    : { width: isFplMigrationEnabled ? FPL_CHANNEL_INPUT_WIDTH : LEGACY_NUMERIC_INPUT_WIDTH };
  return (
    <FlexCenVer sx={fullWidth ? { gap: 0.25, flex: 1, minWidth: 0 } : { gap: 0.25 }}>
      <Input
        disabled={disabled}
        onBlur={onBlur}
        onChange={(e) => updateChannel(0, e.target.value)}
        size="small"
        sx={inputSx}
        value={channels[0] === -1 ? '' : Math.round(channels[0])}
      />
      <Input
        disabled={disabled}
        onBlur={onBlur}
        onChange={(e) => updateChannel(1, e.target.value)}
        size="small"
        sx={inputSx}
        value={channels[1] === -1 ? '' : Math.round(channels[1])}
      />
      <Input
        disabled={disabled}
        onBlur={onBlur}
        onChange={(e) => updateChannel(2, e.target.value)}
        size="small"
        sx={inputSx}
        value={channels[2] === -1 ? '' : Math.round(channels[2])}
      />
    </FlexCenVer>
  );
};

/** Stored as the 0-1 alpha channel and shown as a percentage, as the opacity field is. */
const ALPHA_FORMATTER = new PercentFormatter();

/**
 * Percent field for a color's alpha, shown beside the hex or channel inputs. Built on the same
 * numeric field the opacity property uses, so it takes arrow-key nudges, arithmetic entry, and
 * Enter/Escape rather than only committing a typed number on blur.
 */
export const AlphaEditor = ({
  color,
  onChange,
  onChangeOngoing,
  disabled = false,
  grouped = false,
}: Pick<ColorInputProps, 'color' | 'onChange' | 'onChangeOngoing' | 'disabled' | 'grouped'>) => {
  const { t } = useTranslation();

  // The formatter bounds what it parses, but arithmetic and nudges can still land outside the
  // channel, and zag throws on an out-of-range value.
  const onSubmit = useCallback(
    (value: number, ongoing = false) => {
      const nextColor = color.withChannelValue('alpha', Math.min(1, Math.max(0, value)));
      (ongoing ? (onChangeOngoing ?? onChange) : onChange)(nextColor);
    },
    [color, onChange, onChangeOngoing],
  );

  return (
    <FormattedNumberInput
      ariaLabel={t(I18N_KEYS.COMMON_COMPONENTS.COLOR_PICKER.OPACITY_ARIA)}
      disabled={disabled}
      formatter={ALPHA_FORMATTER}
      onSubmit={onSubmit}
      value={color.getChannelValue('alpha')}
      width={grouped ? '100%' : `${LEGACY_NUMERIC_INPUT_WIDTH}px`}
    />
  );
};

export const ColorInput = ({
  adornment,
  color,
  disabled = false,
  format = 'HEX',
  fullWidth = false,
  grouped = false,
  innerRef,
  isOpen = false,
  onChange,
  onChangeOngoing,
  onColorBoxClick,
  showAlpha = true,
  showColorEditor = true,
  size = 'small',
  withColorBox = false,
}: ColorInputProps) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const colorBoxBackground = isFplMigrationEnabled
    ? disabled
      ? fplTokens.color.bg
      : fplTokens.color.bgSecondary
    : colors.Black92;

  if (isFplMigrationEnabled && grouped && !withColorBox && showColorEditor) {
    return (
      <div style={{ minWidth: 0, width: '100%' }}>
        <FplInput.Group columns={getGroupedInputColumns(format, showAlpha)}>
          {format === 'HEX' ? (
            <HexEditor
              key={color.toString('hexa') + format}
              color={color}
              onChange={onChange}
              disabled={disabled}
              grouped
            />
          ) : (
            <ChannelsEditor
              key={color.toString('rgba') + format}
              color={color.toFormat(format === 'RGB' ? 'rgba' : 'hsla')}
              format={format}
              onChange={onChange}
              disabled={disabled}
              grouped
            />
          )}
          {showAlpha && (
            <AlphaEditor
              color={color}
              onChange={onChange}
              onChangeOngoing={onChangeOngoing}
              disabled={disabled}
              grouped
            />
          )}
        </FplInput.Group>
      </div>
    );
  }

  return (
    <FlexCenVer sx={fullWidth ? { gap: 0.5, width: '100%' } : { gap: 0.5 }}>
      {withColorBox && (
        <Box
          ref={innerRef}
          onClick={onColorBoxClick}
          sx={{
            position: 'relative',
            backgroundColor: colorBoxBackground,
            border: '1px solid',
            borderColor: isFplMigrationEnabled
              ? disabled
                ? fplTokens.color.borderDisabled
                : isOpen
                  ? fplTokens.color.borderSelected
                  : 'transparent'
              : colors.White08_T,
            borderRadius: '4px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: size === 'small' && isFplMigrationEnabled ? '24px' : size === 'small' ? '26px' : '28px',
            width: size === 'small' && isFplMigrationEnabled ? '24px' : size === 'small' ? '26px' : '28px',
            cursor: onColorBoxClick && !disabled ? 'pointer' : 'default',
            transition: (theme) => theme.transitions.create(['border-color', 'background-color']),
            '&:hover': {
              backgroundColor:
                onColorBoxClick && !disabled
                  ? isFplMigrationEnabled
                    ? colorBoxBackground
                    : colors.Black84
                  : colorBoxBackground,
              borderColor:
                isFplMigrationEnabled && onColorBoxClick && !disabled
                  ? isOpen
                    ? fplTokens.color.borderSelected
                    : fplTokens.color.border
                  : undefined,
            },
          }}
        >
          <ColorBox bgColor={color} />
          <Box
            sx={{
              position: 'absolute',
              top: `calc(50% - 5px)`,
              left: `calc(50% - 4px)`,
              width: '8px',
              height: '8px',
              fontSize: '8px',
            }}
          >
            {adornment}
          </Box>
        </Box>
      )}
      <FlexCenVer sx={fullWidth ? { gap: '8px', flex: 1, minWidth: 0 } : { gap: '8px' }}>
        {showColorEditor && (
          <>
            {format === 'HEX' ? (
              <HexEditor
                key={color.toString('hexa') + format}
                color={color}
                onChange={onChange}
                disabled={disabled}
                fullWidth={fullWidth}
              />
            ) : (
              <ChannelsEditor
                key={color.toString('rgba') + format}
                color={color.toFormat(format === 'RGB' ? 'rgba' : 'hsla')}
                onChange={onChange}
                format={format}
                disabled={disabled}
                fullWidth={fullWidth}
              />
            )}
          </>
        )}
        {showAlpha && (
          <AlphaEditor color={color} onChange={onChange} onChangeOngoing={onChangeOngoing} disabled={disabled} />
        )}
      </FlexCenVer>
    </FlexCenVer>
  );
};
